import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { JournalEditor } from "./JournalEditor";
import { getApiBase } from "../apiBase";

const API_BASE = getApiBase();
const JOURNAL_PAGE_SIZE = 5;
const JOURNAL_COPY = {
  en: {
    allTaggedFamily: "All tagged family",
    specificFamilyMember: "Specific family member",
    addTitleOrContent: "Add a title or some content before saving.",
    loadFailed: "Could not load journals",
    saveFailed: "Save failed",
    deleteFailed: "Could not delete this letter.",
    sectionKicker: "Letter journal",
    heading: "Write letters your family can return to.",
    intro:
      "Draft a private letter, choose who it is for, and keep refining it until the right moment to share arrives. The writing surface stays central, while your saved drafts stay easy to revisit.",
    savedLetter: "saved letter",
    savedLetters: "saved letters",
    editingSavedDraft: "Editing saved draft",
    newPrivateDraft: "New private draft",
    yourSavedLetters: "Your saved letters",
    openEarlierDraft: "Open an earlier draft or start a fresh one.",
    newLetter: "New letter",
    deleteLetter: "Delete letter",
    deleteLabel: "Delete",
    deleteConfirmTitle: "Delete this letter?",
    deleteConfirmBody:
      "This will remove the letter from your drafts and it will no longer be visible to the person it was shared with.",
    deleteConfirmAction: "Delete letter",
    cancel: "Cancel",
    searchSavedLetters: "Search saved letters",
    searchPlaceholder: "Search by title, recipient, or text…",
    match: "match",
    matches: "matches",
    page: "Page",
    of: "of",
    openToContinue: "Open this draft to continue writing.",
    noMatchTitle: "No letters match this search.",
    noMatchBody: "Try another title, recipient, or keyword from the draft.",
    noSavedTitle: "No letters saved yet.",
    noSavedBody: "Start with one private letter. You can keep it as a draft until you are ready.",
    sharedWithYou: "Shared with you",
    sharedWithYouIntro: "Letters another trusted person has shared with you.",
    sharedLetter: "shared letter",
    sharedLetters: "shared letters",
    noSharedTitle: "No shared letters yet.",
    noSharedBody: "Letters made available to you will appear here.",
    fromLabel: "From",
    sharedNow: "Shared with you",
    sharedByline: "Shared by",
    sharedAvailableNow: "Available to you now",
    backToDrafts: "Back to your drafts",
    noSharedPreview: "Open this shared letter to read it.",
    previous: "Previous",
    next: "Next",
    editingDraft: "Editing draft",
    newDraft: "New draft",
    startFreshLetter: "Start a fresh letter",
    titleLabel: "Title",
    titlePlaceholder: "e.g. Letter to my children",
    recipientScope: "Recipient scope",
    privateUntilRelease: "Private until release",
    availableNow: "Available now",
    guidancePrivate:
      "Use this space for letters, confessions, or memories. The person you choose here becomes part of the release path you define elsewhere.",
    guidanceAvailable:
      "This letter will stay available to the selected recipient while you are still using AfterMe. Turn it off any time if you want it to wait for your release rules instead.",
    releaseNowLabel: "Release this letter now",
    releaseNowHelp:
      "Turn this on only if you want the selected recipient to access this letter while you are still using AfterMe.",
    entryLabel: "Entry",
    entryPlaceholder: "Write your letter, memory, or final message…",
    saving: "Saving…",
    updateLetter: "Update letter",
    saveLetter: "Save letter",
    saveNotePrivate: "Drafts stay private until your release rules allow access.",
    saveNoteAvailable:
      "This letter is available to the selected recipient now while the rest of your release rules stay in place.",
    noEmailYet: "no email yet",
  },
  fr: {
    allTaggedFamily: "Toute la famille marquée",
    specificFamilyMember: "Membre précis de la famille",
    addTitleOrContent: "Ajoutez un titre ou du contenu avant d’enregistrer.",
    loadFailed: "Impossible de charger les journaux",
    saveFailed: "Échec de l’enregistrement",
    deleteFailed: "Impossible de supprimer cette lettre.",
    sectionKicker: "Journal de lettres",
    heading: "Écrivez des lettres que votre famille pourra relire.",
    intro:
      "Rédigez une lettre privée, choisissez à qui elle est destinée et continuez de l’affiner jusqu’au bon moment pour la partager. L’espace d’écriture reste central, tandis que vos brouillons enregistrés restent faciles à retrouver.",
    savedLetter: "lettre enregistrée",
    savedLetters: "lettres enregistrées",
    editingSavedDraft: "Modification d’un brouillon enregistré",
    newPrivateDraft: "Nouveau brouillon privé",
    yourSavedLetters: "Vos lettres enregistrées",
    openEarlierDraft: "Ouvrez un brouillon précédent ou commencez-en un nouveau.",
    newLetter: "Nouvelle lettre",
    deleteLetter: "Supprimer la lettre",
    deleteLabel: "Supprimer",
    deleteConfirmTitle: "Supprimer cette lettre ?",
    deleteConfirmBody:
      "Cette action retirera la lettre de vos brouillons et elle ne sera plus visible pour la personne avec qui elle était partagée.",
    deleteConfirmAction: "Supprimer la lettre",
    cancel: "Annuler",
    searchSavedLetters: "Rechercher dans les lettres enregistrées",
    searchPlaceholder: "Rechercher par titre, destinataire ou texte…",
    match: "résultat",
    matches: "résultats",
    page: "Page",
    of: "sur",
    openToContinue: "Ouvrez ce brouillon pour continuer à écrire.",
    noMatchTitle: "Aucune lettre ne correspond à cette recherche.",
    noMatchBody: "Essayez un autre titre, destinataire ou mot-clé du brouillon.",
    noSavedTitle: "Aucune lettre enregistrée pour le moment.",
    noSavedBody: "Commencez par une lettre privée. Vous pouvez la garder en brouillon jusqu’au bon moment.",
    sharedWithYou: "Partagé avec vous",
    sharedWithYouIntro: "Lettres qu’une autre personne de confiance a partagées avec vous.",
    sharedLetter: "lettre partagée",
    sharedLetters: "lettres partagées",
    noSharedTitle: "Aucune lettre partagée pour le moment.",
    noSharedBody: "Les lettres rendues accessibles pour vous apparaîtront ici.",
    fromLabel: "De",
    sharedNow: "Partagée avec vous",
    sharedByline: "Partagée par",
    sharedAvailableNow: "Disponible pour vous maintenant",
    backToDrafts: "Retour à vos brouillons",
    noSharedPreview: "Ouvrez cette lettre partagée pour la lire.",
    previous: "Précédent",
    next: "Suivant",
    editingDraft: "Modification du brouillon",
    newDraft: "Nouveau brouillon",
    startFreshLetter: "Commencer une nouvelle lettre",
    titleLabel: "Titre",
    titlePlaceholder: "ex. Lettre à mes enfants",
    recipientScope: "Portée du destinataire",
    privateUntilRelease: "Privée jusqu’à la diffusion",
    availableNow: "Disponible maintenant",
    guidancePrivate:
      "Utilisez cet espace pour des lettres, des confidences ou des souvenirs. La personne choisie ici fait partie du parcours de diffusion que vous définissez ailleurs.",
    guidanceAvailable:
      "Cette lettre restera accessible au destinataire sélectionné pendant que vous utilisez encore AfterMe. Vous pouvez désactiver cette option à tout moment si vous préférez attendre vos règles de diffusion.",
    releaseNowLabel: "Diffuser cette lettre maintenant",
    releaseNowHelp:
      "Activez cette option uniquement si vous souhaitez que le destinataire sélectionné puisse accéder à cette lettre pendant que vous utilisez encore AfterMe.",
    entryLabel: "Contenu",
    entryPlaceholder: "Écrivez votre lettre, souvenir ou message final…",
    saving: "Enregistrement…",
    updateLetter: "Mettre à jour la lettre",
    saveLetter: "Enregistrer la lettre",
    saveNotePrivate: "Les brouillons restent privés jusqu’à ce que vos règles de diffusion permettent l’accès.",
    saveNoteAvailable:
      "Cette lettre est accessible au destinataire sélectionné maintenant, tandis que le reste de vos règles de diffusion reste en place.",
    noEmailYet: "aucun courriel pour l’instant",
  },
};

function stripJournalPreview(html) {
  return String(html || "")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function formatJournalTimestamp(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function recipientLabelForKey(recipientKey, options, copy) {
  if (!recipientKey || recipientKey === "all-family") {
    return copy.allTaggedFamily;
  }
  const match = options.find(
    (option) =>
      String(option.id) === String(recipientKey) ||
      String(option.backendNodeId || "") === String(recipientKey)
  );
  return match ? `${match.name} (${match.relation})` : copy.specificFamilyMember;
}

export function JournalPanel({
  authToken,
  selectedRecipient,
  onRecipientChange,
  recipientOptions,
  initialOpenJournalId = "",
  onError = null,
  locale = "en",
}) {
  const copy = JOURNAL_COPY[locale === "fr" ? "fr" : "en"];
  const [journals, setJournals] = useState([]);
  const [sharedJournals, setSharedJournals] = useState([]);
  const [title, setTitle] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [activeSharedJournalId, setActiveSharedJournalId] = useState(null);
  const [initialHtml, setInitialHtml] = useState("<p></p>");
  const [releaseNow, setReleaseNow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingJournalId, setDeletingJournalId] = useState("");
  const [confirmDeleteJournal, setConfirmDeleteJournal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingShared, setLoadingShared] = useState(false);
  const [localError, setLocalError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [draftPage, setDraftPage] = useState(1);
  const editorInstanceRef = useRef(null);
  const autoOpenedJournalRef = useRef("");

  const loadJournals = useCallback(async () => {
    if (!authToken) {
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/journals`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || copy.loadFailed);
      }
      setJournals(
        (Array.isArray(data.items) ? data.items : []).map((item) => ({
          ...item,
          releaseNow: Boolean(item.releaseNow),
        }))
      );
    } catch (e) {
      setLocalError(e.message);
      if (onError) {
        onError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [authToken, copy.loadFailed, onError]);

  const loadSharedJournals = useCallback(async () => {
    if (!authToken) {
      return;
    }
    setLoadingShared(true);
    try {
      const res = await fetch(`${API_BASE}/api/journals/shared-with-me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || copy.loadFailed);
      }
      setSharedJournals(
        (Array.isArray(data.items) ? data.items : []).map((item) => ({
          ...item,
          releaseNow: Boolean(item.releaseNow),
        }))
      );
    } catch (e) {
      setLocalError(e.message);
      if (onError) {
        onError(e.message);
      }
    } finally {
      setLoadingShared(false);
    }
  }, [authToken, copy.loadFailed, onError]);

  useEffect(() => {
    loadJournals();
    loadSharedJournals();
  }, [loadJournals, loadSharedJournals]);

  const filteredJournals = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) {
      return journals;
    }
    return journals.filter((journal) => {
      const preview = stripJournalPreview(journal.htmlBody);
      const recipient = recipientLabelForKey(journal.recipientKey, recipientOptions, copy);
      return [journal.title, preview, recipient].some((value) =>
        String(value || "").toLowerCase().includes(needle)
      );
    });
  }, [copy, journals, recipientOptions, searchTerm]);

  const totalDraftPages = Math.max(1, Math.ceil(filteredJournals.length / JOURNAL_PAGE_SIZE));
  const currentDraftPage = Math.min(draftPage, totalDraftPages);

  const paginatedJournals = useMemo(() => {
    const start = (currentDraftPage - 1) * JOURNAL_PAGE_SIZE;
    return filteredJournals.slice(start, start + JOURNAL_PAGE_SIZE);
  }, [currentDraftPage, filteredJournals]);

  useEffect(() => {
    setDraftPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (draftPage > totalDraftPages) {
      setDraftPage(totalDraftPages);
    }
  }, [draftPage, totalDraftPages]);

  const resetDraft = useCallback(() => {
    setLocalError("");
    setEditingId(null);
    setActiveSharedJournalId(null);
    setTitle("");
    setInitialHtml("<p></p>");
    setReleaseNow(false);
    if (onRecipientChange) {
      onRecipientChange("all-family");
    }
    const ed = editorInstanceRef.current;
    if (ed) {
      ed.commands.setContent("<p></p>");
    }
  }, [onRecipientChange]);

  const saveJournal = useCallback(async () => {
    const editor = editorInstanceRef.current;
    if (!editor || !authToken) {
      return;
    }
    const htmlBody = editor.getHTML();
    const plain = editor.getText().trim();
    if (!title.trim() && !plain) {
      setLocalError(copy.addTitleOrContent);
      return;
    }
    setLocalError("");
    setSaving(true);
    try {
      const normalizedRecipientKey =
        selectedRecipient === "all-family"
          ? "all-family"
          : recipientOptions.find(
                (option) =>
                  String(option.id) === String(selectedRecipient) ||
                  String(option.backendNodeId || "") === String(selectedRecipient)
              )?.backendNodeId || selectedRecipient;
      const payload = {
        title: title.trim() || "Untitled entry",
        htmlBody,
        recipientKey: normalizedRecipientKey,
        releaseNow,
      };
      const url = editingId
        ? `${API_BASE}/api/journals/${editingId}`
        : `${API_BASE}/api/journals`;
      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || copy.saveFailed);
      }
      await loadJournals();
      await loadSharedJournals();
      resetDraft();
    } catch (e) {
      setLocalError(e.message);
      if (onError) {
        onError(e.message);
      }
    } finally {
      setSaving(false);
    }
  }, [
    authToken,
    copy.addTitleOrContent,
    copy.saveFailed,
    editingId,
    loadJournals,
    loadSharedJournals,
    onError,
    releaseNow,
    resetDraft,
    recipientOptions,
    selectedRecipient,
    title,
  ]);

  const deleteJournal = useCallback(
    async (journal) => {
      if (!journal?.journalId || !authToken) {
        return;
      }
      setLocalError("");
      setDeletingJournalId(journal.journalId);
      try {
        const res = await fetch(`${API_BASE}/api/journals/${journal.journalId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || copy.deleteFailed);
        }
        await loadJournals();
        await loadSharedJournals();
        if (editingId === journal.journalId) {
          resetDraft();
        }
        setConfirmDeleteJournal(null);
      } catch (e) {
        setLocalError(e.message);
        if (onError) {
          onError(e.message);
        }
      } finally {
        setDeletingJournalId("");
      }
    },
    [authToken, copy.deleteFailed, editingId, loadJournals, loadSharedJournals, onError, resetDraft]
  );

  const openJournal = useCallback((item) => {
    setActiveSharedJournalId(null);
    setEditingId(item.journalId);
    setTitle(item.title || "");
    setInitialHtml(item.htmlBody || "<p></p>");
    setReleaseNow(Boolean(item.releaseNow));
    if (onRecipientChange) {
      onRecipientChange(item.recipientKey || "all-family");
    }
  }, [onRecipientChange]);

  const openSharedJournal = useCallback((item) => {
    setLocalError("");
    setEditingId(null);
    setActiveSharedJournalId(item.journalId);
  }, []);

  useEffect(() => {
    if (!initialOpenJournalId || !journals.length) {
      return;
    }
    if (autoOpenedJournalRef.current === initialOpenJournalId && editingId === initialOpenJournalId) {
      return;
    }
    const target = journals.find((item) => item.journalId === initialOpenJournalId);
    if (!target) {
      return;
    }
    autoOpenedJournalRef.current = initialOpenJournalId;
    openJournal(target);
  }, [editingId, initialOpenJournalId, journals, openJournal]);

  const activeSharedJournal = useMemo(
    () => sharedJournals.find((item) => item.journalId === activeSharedJournalId) || null,
    [activeSharedJournalId, sharedJournals]
  );

  return (
    <section className="panel journal-workspace-panel" id="journal">
      <div className="journal-workspace-head">
          <div className="journal-workspace-copy">
          <p className="journal-section-kicker">{copy.sectionKicker}</p>
          <h2>{copy.heading}</h2>
          <p>
            {copy.intro}
          </p>
        </div>
        <div className="journal-workspace-summary" aria-label="Journal overview">
          <span className="journal-summary-pill">
            {journals.length} {journals.length === 1 ? copy.savedLetter : copy.savedLetters}
          </span>
          <span className="journal-summary-pill">
            {editingId ? copy.editingSavedDraft : copy.newPrivateDraft}
          </span>
        </div>
      </div>

      <div className="journal-workspace-layout">
        <aside className="journal-sidebar" aria-label="Saved journal drafts">
          <div className="journal-sidebar-head">
            <div>
              <h3>Your saved letters</h3>
              <p className="helper-text">Open an earlier draft or start a fresh one.</p>
            </div>
            <button type="button" className="journal-ghost-btn" onClick={resetDraft}>
              New letter
            </button>
          </div>

          <div className="journal-sidebar-tools">
            <label className="journal-search-label" htmlFor="journal-draft-search">
              Search saved letters
            </label>
            <input
              id="journal-draft-search"
              className="journal-search-input"
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by title, recipient, or text…"
            />
            <div className="journal-sidebar-stats">
              <span className="helper-text">
                {filteredJournals.length} {filteredJournals.length === 1 ? "match" : "matches"}
              </span>
              {filteredJournals.length > 0 ? (
                <span className="helper-text">
                  Page {currentDraftPage} of {totalDraftPages}
                </span>
              ) : null}
            </div>
          </div>

          {loading ? (
            <p className="helper-text">Loading…</p>
          ) : filteredJournals.length > 0 ? (
            <ul className="journal-draft-list">
              {paginatedJournals.map((journal) => {
                const isActive = editingId === journal.journalId;
                const preview = stripJournalPreview(journal.htmlBody).slice(0, 120);
                return (
                  <li key={journal.journalId}>
                    <div className={`journal-draft-card ${isActive ? "is-active" : ""}`}>
                      <button
                        type="button"
                        className="journal-draft-card-main"
                        onClick={() => openJournal(journal)}
                      >
                      <span className="journal-draft-meta">
                        <span className="journal-draft-recipient">
                          {recipientLabelForKey(journal.recipientKey, recipientOptions, copy)}
                        </span>
                        <span className="journal-draft-date">
                          {formatJournalTimestamp(journal.updatedAt || journal.createdAt)}
                        </span>
                      </span>
                      <span
                        className={`journal-draft-release-badge ${
                          journal.releaseNow ? "is-live" : "is-private"
                        }`}
                      >
                        {journal.releaseNow ? copy.availableNow : copy.privateUntilRelease}
                      </span>
                      <strong>{journal.title}</strong>
                      <span className="journal-draft-preview">
                        {preview || copy.openToContinue}
                      </span>
                      </button>
                      <button
                        type="button"
                        className="journal-draft-delete-btn"
                        aria-label={copy.deleteLetter}
                        title={copy.deleteLetter}
                        onClick={() => setConfirmDeleteJournal(journal)}
                      >
                        <span aria-hidden="true">🗑</span>
                        <span>{copy.deleteLabel}</span>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : journals.length > 0 ? (
            <div className="journal-empty-state">
              <strong>{copy.noMatchTitle}</strong>
              <p className="helper-text">{copy.noMatchBody}</p>
            </div>
          ) : (
            <div className="journal-empty-state">
              <strong>{copy.noSavedTitle}</strong>
              <p className="helper-text">{copy.noSavedBody}</p>
            </div>
          )}

          {filteredJournals.length > JOURNAL_PAGE_SIZE ? (
            <div className="journal-pagination" aria-label="Saved letter pages">
              <button
                type="button"
                className="journal-pagination-btn"
                onClick={() => setDraftPage((page) => Math.max(1, page - 1))}
                disabled={currentDraftPage === 1}
              >
                {copy.previous}
              </button>
              <span className="journal-pagination-status">
                {Math.min((currentDraftPage - 1) * JOURNAL_PAGE_SIZE + 1, filteredJournals.length)}
                –
                {Math.min(currentDraftPage * JOURNAL_PAGE_SIZE, filteredJournals.length)} {copy.of}{" "}
                {filteredJournals.length}
              </span>
              <button
                type="button"
                className="journal-pagination-btn"
                onClick={() => setDraftPage((page) => Math.min(totalDraftPages, page + 1))}
                disabled={currentDraftPage === totalDraftPages}
              >
                {copy.next}
              </button>
            </div>
          ) : null}

          <div className="journal-shared-section">
            <div className="journal-sidebar-head journal-sidebar-head--stacked">
              <div>
                <h3>{copy.sharedWithYou}</h3>
                <p className="helper-text">{copy.sharedWithYouIntro}</p>
              </div>
              <span className="journal-summary-pill">
                {sharedJournals.length}{" "}
                {sharedJournals.length === 1 ? copy.sharedLetter : copy.sharedLetters}
              </span>
            </div>

            {loadingShared ? (
              <p className="helper-text">Loading…</p>
            ) : sharedJournals.length > 0 ? (
              <ul className="journal-draft-list">
                {sharedJournals.map((journal) => {
                  const isActive = activeSharedJournalId === journal.journalId;
                  const preview = stripJournalPreview(journal.htmlBody).slice(0, 120);
                  const senderName = journal.sharedByName || copy.specificFamilyMember;
                  return (
                    <li key={`shared-${journal.journalId}`}>
                      <button
                        type="button"
                        className={`journal-draft-card journal-draft-card--shared ${isActive ? "is-active" : ""}`}
                        onClick={() => openSharedJournal(journal)}
                      >
                        <span className="journal-shared-card-head">
                          <span className="journal-shared-card-sender">
                            <span className="journal-shared-card-label">{copy.fromLabel}</span>
                            <span className="journal-shared-card-name">{senderName}</span>
                          </span>
                          <span className="journal-draft-release-badge is-live">{copy.sharedNow}</span>
                        </span>
                        <span className="journal-shared-card-date">
                          {formatJournalTimestamp(
                            journal.sharedAt || journal.updatedAt || journal.createdAt
                          )}
                        </span>
                        <strong className="journal-shared-card-title">{journal.title}</strong>
                        <span className="journal-draft-preview journal-shared-card-preview">
                          {preview || copy.noSharedPreview}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="journal-empty-state">
                <strong>{copy.noSharedTitle}</strong>
                <p className="helper-text">{copy.noSharedBody}</p>
              </div>
            )}
          </div>
        </aside>

        <div className="journal-composer-card">
          {activeSharedJournal ? (
            <div className="journal-shared-reader">
              <div className="journal-composer-top">
                <div className="journal-status-stack">
                  <span className="journal-state-pill journal-release-pill is-live">
                    {copy.sharedNow}
                  </span>
                  <span className="journal-state-pill journal-recipient-pill">
                    {copy.sharedByline} {activeSharedJournal.sharedByName || copy.specificFamilyMember}
                  </span>
                  <span className="journal-state-pill journal-release-pill is-live">
                    {copy.sharedAvailableNow}
                  </span>
                </div>
                <button type="button" className="journal-ghost-btn" onClick={resetDraft}>
                  {copy.backToDrafts}
                </button>
              </div>

              <div className="journal-shared-header">
                <h3>{activeSharedJournal.title}</h3>
                <p className="helper-text">
                  {copy.fromLabel} {activeSharedJournal.sharedByName || copy.specificFamilyMember} ·{" "}
                  {formatJournalTimestamp(
                    activeSharedJournal.sharedAt ||
                      activeSharedJournal.updatedAt ||
                      activeSharedJournal.createdAt
                  )}
                </p>
              </div>

              <article
                className="journal-shared-body rich-text-render"
                dangerouslySetInnerHTML={{ __html: activeSharedJournal.htmlBody || "<p></p>" }}
              />
            </div>
          ) : (
            <>
          <div className="journal-composer-top">
            <div className="journal-status-stack">
              <span className={`journal-state-pill ${editingId ? "is-editing" : "is-new"}`}>
                {editingId ? copy.editingDraft : copy.newDraft}
              </span>
              <span className="journal-state-pill journal-recipient-pill">
                {recipientLabelForKey(selectedRecipient, recipientOptions, copy)}
              </span>
              <span
                className={`journal-state-pill journal-release-pill ${
                  releaseNow ? "is-live" : "is-private"
                }`}
              >
                {releaseNow ? copy.availableNow : copy.privateUntilRelease}
              </span>
            </div>
            {editingId ? (
              <button type="button" className="journal-ghost-btn" onClick={resetDraft}>
                {copy.startFreshLetter}
              </button>
            ) : null}
          </div>

          <label htmlFor="journal-title" className="field-label">
            {copy.titleLabel}
          </label>
          <input
            id="journal-title"
            className="journal-title-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={copy.titlePlaceholder}
          />

          <div className="journal-meta-grid">
            <div className="journal-field-block">
              <label htmlFor="journal-recipient" className="field-label">
                {copy.recipientScope}
              </label>
              <select
                id="journal-recipient"
                value={selectedRecipient}
                onChange={(e) => onRecipientChange(e.target.value)}
              >
                <option value="all-family">{copy.allTaggedFamily}</option>
                {recipientOptions.map((m) => (
                  <option key={m.id} value={m.backendNodeId || m.id}>
                    {m.name} ({m.relation})
                    {!m.email ? ` — ${copy.noEmailYet}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="journal-guidance-card">
              <strong>{releaseNow ? copy.availableNow : copy.privateUntilRelease}</strong>
              <p>{releaseNow ? copy.guidanceAvailable : copy.guidancePrivate}</p>
            </div>
          </div>

          <label className="journal-release-toggle" htmlFor="journal-release-now">
            <input
              id="journal-release-now"
              type="checkbox"
              checked={releaseNow}
              onChange={(event) => setReleaseNow(event.target.checked)}
            />
            <span>
              <strong>{copy.releaseNowLabel}</strong>
              <small>{copy.releaseNowHelp}</small>
            </span>
          </label>

          {localError && <p className="error-text">{localError}</p>}

          <label className="field-label">{copy.entryLabel}</label>
          <JournalEditor
            key={editingId || "new"}
            initialHtml={initialHtml}
            placeholder={copy.entryPlaceholder}
            editorRef={(ed) => {
              editorInstanceRef.current = ed;
            }}
          />

          <div className="journal-actions">
            <button type="button" onClick={saveJournal} disabled={saving}>
              {saving ? copy.saving : editingId ? copy.updateLetter : copy.saveLetter}
            </button>
            <span className="helper-text journal-save-note">
              {releaseNow ? copy.saveNoteAvailable : copy.saveNotePrivate}
            </span>
          </div>
            </>
          )}
        </div>
      </div>

      {confirmDeleteJournal ? (
        <div className="modal-backdrop" role="presentation" onClick={() => setConfirmDeleteJournal(null)}>
          <div
            className="modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="journal-delete-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="journal-delete-title">{copy.deleteConfirmTitle}</h2>
            <p className="modal-lead">{copy.deleteConfirmBody}</p>
            <div className="modal-actions">
              <button
                type="button"
                className="family-delete-cancel-btn"
                onClick={() => setConfirmDeleteJournal(null)}
                disabled={deletingJournalId === confirmDeleteJournal.journalId}
              >
                {copy.cancel}
              </button>
              <button
                type="button"
                className="family-delete-confirm-btn"
                onClick={() => deleteJournal(confirmDeleteJournal)}
                disabled={deletingJournalId === confirmDeleteJournal.journalId}
              >
                {deletingJournalId === confirmDeleteJournal.journalId
                  ? copy.saving
                  : copy.deleteConfirmAction}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
